import {
  anafAlerts,
  anafAsOf,
  cleanCui,
  countiesText,
  cuiProblem,
  roDateTime,
  type AnafInfo,
  type Supplier,
  type SupplierContactInput,
  t,
  LANGS,
  langOf,
  type Lang,
} from '@procumi/shared';

/** Mesajul de sub CUI, după ce anume nu se potrivește. */
const CUI_MSG = {
  format: 'Un CUI are între 2 și 10 cifre, cu sau fără RO în față.',
  control: t(
    'Ultima cifră (cifra de control) nu se potrivește cu celelalte. Verifică-l pe o factură sau pe anaf.ro.',
  ),
} as const;
import { useState } from 'react';
import { companyLocale } from '../lib/me';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { legal } from '../lib/market';
import { CategoryPicker } from './CategoryPicker';
import { confirmDiscard, Dialog } from './Dialog';
import { ask } from '../lib/confirm';

interface ContactRow extends SupplierContactInput {
  /** cheie stabilă pentru React, și la persoanele încă nesalvate */
  key: string;
}

let seq = 0;
const emptyContact = (): ContactRow => ({
  key: `new${++seq}`,
  name: '',
  email: '',
  phone: '',
  role: '',
  isPrimary: false,
});

export function SupplierDialog({
  supplier,
  suggestions,
  defaultCategory,
  toast,
  onSaved,
  onClose,
}: {
  /** lipsă la adăugare, prezent la editare */
  supplier?: Supplier;
  /** categoriile deja folosite de firmă */
  suggestions: string[];
  /** categoria din filtrul curent, propusă la un furnizor nou */
  defaultCategory?: string;
  toast: Toast;
  onSaved: (supplier: Supplier) => void;
  onClose: () => void;
}) {
  const editing = supplier != null;
  const L = legal();
  const [name, setName] = useState(supplier?.name ?? '');
  const [cui, setCui] = useState(supplier?.cui ?? '');
  const cuiIssue = L.checkCui && cui.trim() !== '' && cuiProblem(cui) !== '';
  const [address, setAddress] = useState(supplier?.address ?? '');
  // limba în care primește cererile și comenzile; implicit, a firmei
  const [locale, setLocale] = useState<Lang>(supplier?.locale ?? langOf(companyLocale().slice(0, 2)));
  // starea de la ANAF și CUI-ul pentru care e: dacă schimbi CUI-ul, nu mai e a lui
  const [anaf, setAnaf] = useState<{ cui: string; info: AnafInfo } | null>(
    supplier?.anaf ? { cui: cleanCui(supplier.cui), info: supplier.anaf } : null,
  );
  const [anafBusy, setAnafBusy] = useState(false);
  const cuiReady = L.checkCui && cui.trim() !== '' && cuiProblem(cui) === '';
  const anafNow = anaf && anaf.cui === cleanCui(cui) ? anaf.info : null;

  /** Întreabă ANAF; numele și adresa goale se completează singure. */
  const lookup = async () => {
    if (!cuiReady || anafBusy) return;
    const key = cleanCui(cui);
    setAnafBusy(true);
    try {
      const info = await api.anafLookup(key);
      setAnaf({ cui: key, info });
      if (info.found) {
        if (!name.trim()) setName(info.name);
        if (!address.trim()) setAddress(info.address);
      }
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setAnafBusy(false);
    }
  };
  const [categories, setCategories] = useState<string[]>(
    supplier?.categories ?? (defaultCategory ? [defaultCategory] : []),
  );
  const [contacts, setContacts] = useState<ContactRow[]>(() =>
    supplier?.contacts.length
      ? supplier.contacts.map((c) => ({
          key: c.id,
          id: c.id,
          name: c.name,
          email: c.email,
          phone: c.phone,
          role: c.role,
          isPrimary: c.isPrimary,
        }))
      : [{ ...emptyContact(), isPrimary: true }],
  );
  const [busy, setBusy] = useState(false);

  // Cheile persoanelor nu contează: sunt doar pentru React.
  const form = JSON.stringify([
    name,
    cui,
    address,
    locale,
    categories,
    contacts.map(({ key: _, ...c }) => c),
  ]);
  const [initial] = useState(form);
  const dirty = form !== initial;
  const cancel = () => {
    void confirmDiscard(dirty).then((ok) => ok && onClose());
  };

  const patchContact = (key: string, fields: Partial<ContactRow>) =>
    setContacts((list) => list.map((c) => (c.key === key ? { ...c, ...fields } : c)));

  const setPrimary = (key: string) =>
    setContacts((list) => list.map((c) => ({ ...c, isPrimary: c.key === key })));

  const removeContact = (key: string) =>
    setContacts((list) => {
      const left = list.filter((c) => c.key !== key);
      return left.some((c) => c.isPrimary) || !left.length
        ? left
        : left.map((c, i) => ({ ...c, isPrimary: i === 0 }));
    });

  const save = async () => {
    if (!name.trim()) return toast(t('Completează numele firmei'));
    // CUI-ul și adresa nu se cer aici: pentru o cerere de ofertă ajung numele și
    // emailul. Le cere comanda, unde chiar apar, și de acolo rămân în agendă.
    const problem = cui.trim() && L.checkCui ? cuiProblem(cui) : '';
    if (problem === 'format') return toast(CUI_MSG.format);
    // Cifra de control greșită e aproape mereu o greșeală de tastare, dar omul
    // poate ști mai bine (un CUI vechi, un caz rar): întrebăm, nu blocăm.
    if (
      problem === 'control' &&
      !(await ask({
        title: t('CUI-ul pare greșit'),
        message: t(
          '{0}: ultima cifră (cifra de control) nu se potrivește cu celelalte, de obicei semn că o cifră e tastată greșit. Dacă știi că e corect, îl poți salva așa.',
          cleanCui(cui),
        ),
        confirm: t('Salvează așa'),
        cancel: t('Corectez'),
      }))
    ) {
      return;
    }
    if (!categories.length) return toast(t('Alege cel puțin o categorie'));

    const filled = contacts.filter((c) => c.email?.trim() || c.name?.trim());
    if (!filled.length) return toast(t('Adaugă cel puțin o persoană de contact'));
    for (const c of filled) {
      if (!/^\S+@\S+\.\S+$/.test((c.email ?? '').trim())) {
        return toast(
          c.name?.trim()
            ? t('Completează un email valid pentru {0}', c.name.trim())
            : t('Completează un email valid'),
        );
      }
    }

    const payload = {
      name: name.trim(),
      cui: cleanCui(cui),
      address: address.trim(),
      locale,
      categories,
      contacts: filled.map((c) => ({
        ...(c.id ? { id: c.id } : {}),
        name: (c.name ?? '').trim(),
        email: (c.email ?? '').trim(),
        phone: (c.phone ?? '').trim(),
        role: (c.role ?? '').trim(),
        isPrimary: !!c.isPrimary,
      })),
    };

    setBusy(true);
    try {
      const saved = editing
        ? await api.updateSupplier(supplier.id, payload)
        : await api.createSupplier(payload);
      onSaved(saved);
      onClose();
      toast(editing ? t('Furnizor actualizat') : t('Furnizor adăugat'));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} dirty={dirty}>
      <div className="dlg-h">
        <div>
          <h2>{editing ? t('Editează {0}', supplier.name) : t('Furnizor nou')}</h2>
          {editing && supplier.sent > 0 ? (
            <p className="hint">
              {t(
                'Furnizorul apare în {0} {1}. Numele corectat se vede și acolo.',
                supplier.sent,
                supplier.sent === 1 ? t('cerere') : t('cereri'),
              )}
            </p>
          ) : (
            <p className="hint">{t('Un distribuitor poate avea mai multe categorii și mai mulți oameni.')}</p>
          )}
        </div>
      </div>

      <div className="dlg-b">
        <label className="f" htmlFor="sup-name">
          {t('Firmă')}
          <input
            id="sup-name"
            className="inp"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <div className="row">
          <label className="f" htmlFor="sup-cui">
            {t('{0} (opțional)', L.taxId)}
            <input
              id="sup-cui"
              className="inp"
              value={cui}
              placeholder={L.checkCui ? 'RO12345678' : undefined}
              maxLength={20}
              aria-invalid={cuiIssue}
              onChange={(e) => setCui(e.target.value)}
              onBlur={() => {
                if (cuiReady && !anafNow) void lookup();
              }}
            />
            {cuiIssue ? (
              <span className="hint warn-text">{CUI_MSG[cuiProblem(cui) as 'format' | 'control']}</span>
            ) : (
              <span className="hint">
                {t(
                  'Îl poți lăsa gol: îl cere comanda, dacă lipsește.{0}',
                  L.checkCui ? t(' Numele și adresa vin de la ANAF.') : '',
                )}
              </span>
            )}
          </label>
          <label className="f" htmlFor="sup-address">
            {t('Adresă sediu (opțional)')}
            <input
              id="sup-address"
              className="inp"
              value={address}
              maxLength={300}
              placeholder={t('Str. Zizinului 110, Brașov')}
              onChange={(e) => setAddress(e.target.value)}
            />
          </label>
        </div>

        {L.checkCui && (anafNow || anafBusy || cuiReady) ? (
          <AnafBox
            info={anafNow}
            busy={anafBusy}
            differs={!!anafNow?.found && (name.trim() !== anafNow.name || address.trim() !== anafNow.address)}
            onLookup={() => void lookup()}
            onUse={() => {
              if (!anafNow) return;
              setName(anafNow.name);
              setAddress(anafNow.address);
            }}
          />
        ) : null}

        {supplier?.selfFilledAt ? (
          <p className="hint" style={{ margin: 0 }}>
            {t(
              'Furnizorul și-a trecut singur datele pe {0}, după oferta prin link{1}',
              roDateTime(supplier.selfFilledAt),
              supplier.counties.length ? t('. Livrează în: {0}.', countiesText(supplier.counties)) : '.',
            )}
          </p>
        ) : null}

        <label className="f" htmlFor="sup-lang">
          {t('Limba mailurilor către el')}
          <select
            id="sup-lang"
            className="inp"
            value={locale}
            onChange={(e) => setLocale(langOf(e.target.value))}
          >
            {LANGS.map((l) => (
              <option key={l.id} value={l.id}>
                {l.label}
              </option>
            ))}
          </select>
          <span className="muted">{t('Cererea, mementourile, comanda și pagina ofertei.')}</span>
        </label>

        <div className="f">
          {t('Categorii')}
          <CategoryPicker value={categories} suggestions={suggestions} onChange={setCategories} />
        </div>

        <div className="f">
          {t('Persoane de contact')}
          <div className="contacts">
            {contacts.map((c) => (
              <div className="contact-row" key={c.key}>
                <div className="row">
                  <input
                    className="inp"
                    value={c.name ?? ''}
                    placeholder={t('Nume')}
                    aria-label={t('Nume persoană de contact')}
                    onChange={(e) => patchContact(c.key, { name: e.target.value })}
                  />
                  <input
                    className="inp"
                    type="email"
                    value={c.email ?? ''}
                    placeholder={t('Email')}
                    aria-label={t('Email persoană de contact')}
                    onChange={(e) => patchContact(c.key, { email: e.target.value })}
                  />
                  <input
                    className="inp"
                    value={c.phone ?? ''}
                    placeholder={t('Telefon')}
                    aria-label={t('Telefon persoană de contact')}
                    onChange={(e) => patchContact(c.key, { phone: e.target.value })}
                  />
                  <input
                    className="inp"
                    value={c.role ?? ''}
                    placeholder={t('Se ocupă de (ex. HVAC)')}
                    aria-label={t('Gama de care se ocupă')}
                    onChange={(e) => patchContact(c.key, { role: e.target.value })}
                  />
                </div>
                <div className="contact-foot">
                  <label className="primary-pick">
                    <input
                      type="radio"
                      name="primary-contact"
                      checked={!!c.isPrimary}
                      onChange={() => setPrimary(c.key)}
                    />
                    {t('Propus implicit la cereri')}
                  </label>
                  {contacts.length > 1 ? (
                    <button type="button" className="x" onClick={() => removeContact(c.key)}>
                      {t('Scoate persoana')}
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn small"
            style={{ marginTop: 8, justifySelf: 'start' }}
            onClick={() => setContacts((list) => [...list, emptyContact()])}
          >
            {t('Adaugă persoană')}
          </button>
        </div>
      </div>

      <div className="dlg-f">
        <button className="btn" onClick={cancel}>
          {t('Anulează')}
        </button>
        <button className="btn primary" disabled={busy} onClick={() => void save()}>
          {editing ? t('Salvează') : t('Adaugă furnizor')}
        </button>
      </div>
    </Dialog>
  );
}

/** Ce spune ANAF despre CUI: firma, starea fiscală și data verificării. */
function AnafBox({
  info,
  busy,
  differs,
  onLookup,
  onUse,
}: {
  info: AnafInfo | null;
  busy: boolean;
  /** numele sau adresa din formular diferă de cele de la ANAF */
  differs: boolean;
  onLookup: () => void;
  onUse: () => void;
}) {
  if (!info) {
    return (
      <div className="anaf-box">
        <button className="btn small" disabled={busy} onClick={onLookup}>
          {busy ? t('Caut la ANAF…') : t('Caută la ANAF')}
        </button>
      </div>
    );
  }
  const alerts = anafAlerts(info);
  const bad = alerts.some((a) => a.level === 'danger');
  return (
    <div className={`anaf-box${bad ? ' bad' : ''}`} role={bad ? 'alert' : undefined}>
      {info.found ? (
        <div>
          <b>{info.name}</b>
          {info.regCom ? <span className="muted"> · {info.regCom}</span> : null}
          <div className="muted">{info.address}</div>
        </div>
      ) : null}
      {alerts.length ? (
        <ul>
          {alerts.map((a) => (
            <li key={a.text} className={a.level === 'danger' ? 'bad' : undefined}>
              {a.text}
            </li>
          ))}
        </ul>
      ) : (
        <div className="anaf-ok">{t('Activă, plătitoare de TVA.')}</div>
      )}
      <div className="anaf-foot">
        <span className="muted">{anafAsOf(info)}</span>
        {differs ? (
          <button className="link" onClick={onUse}>
            {t('Folosește numele și adresa de la ANAF')}
          </button>
        ) : null}
        <button className="link" disabled={busy} onClick={onLookup}>
          {busy ? t('Verific…') : t('Verifică din nou')}
        </button>
      </div>
    </div>
  );
}
